export const COMPANY_LOGO_MAX_SIZE_BYTES = 30 * 1024 * 1024;
export const COMPANY_LOGO_ACCEPT = '.jpg,.png,.svg';
export const DEFAULT_COMPANY_LOGO_FILE_ID = 'brainiacs-default-logo';
export const TEAM_LOGO_URL_PREFERENCE_KEY = 'logo_url';
export const LEGACY_TEAM_LOGO_URL_PREFERENCE_KEY = 'company_logo_url';

/**
 * ALL brand logos — the Brainiacs default AND any logo uploaded through the
 * Branding tab — render monochrome white. The header/settings sit on dark
 * glass surfaces, so colored logos would clash and light logos would vanish.
 * `brightness-0 invert` flattens every logo to pure white.
 */
export const renderCompanyLogoInWhite = (): boolean => true;

export const getCompanyLogoUrlFromTeamPreferences = (preferences: Record<string, unknown>): string | undefined => {
  const logoUrl = preferences[TEAM_LOGO_URL_PREFERENCE_KEY];
  if (typeof logoUrl === 'string' && logoUrl.trim()) return logoUrl;

  const legacyLogoUrl = preferences[LEGACY_TEAM_LOGO_URL_PREFERENCE_KEY];
  return typeof legacyLogoUrl === 'string' && legacyLogoUrl.trim() ? legacyLogoUrl : undefined;
};

export const getCompanyLogoPreferenceUpdate = (url: string): { logo_url: string } => ({
  [TEAM_LOGO_URL_PREFERENCE_KEY]: url,
});

const ALLOWED_MIME_TYPES: Record<string, readonly string[]> = {
  jpg: ['image/jpeg'],
  png: ['image/png'],
  svg: ['image/svg+xml'],
};

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

type LogoFile = Pick<File, 'name' | 'size' | 'type' | 'slice'>;

export const getCompanyLogoExtension = (filename: string): 'jpg' | 'png' | 'svg' | null => {
  const extension = filename.split('.').pop()?.toLowerCase();
  return extension === 'jpg' || extension === 'png' || extension === 'svg' ? extension : null;
};

export const formatCompanyLogoSize = (size: number): string => {
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const getTeamLogoFilename = (teamId: string, sourceFilename: string): string => {
  const extension = getCompanyLogoExtension(sourceFilename);
  if (!extension) throw new Error('Cannot create a team logo filename for an unsupported file type.');
  return `${teamId}.${extension}`;
};

export const getCompanyLogoMetadataError = (file: Pick<LogoFile, 'name' | 'size' | 'type'>): string | null => {
  const extension = getCompanyLogoExtension(file.name);
  if (!extension) {
    return 'Choose a JPG, PNG, or SVG file. JPEG files must use the .jpg extension.';
  }

  if (file.size <= 0) return 'This file is empty. Choose a valid company logo.';
  if (file.size > COMPANY_LOGO_MAX_SIZE_BYTES) {
    return 'The logo is larger than Appwrite’s 30 MB limit.';
  }

  const allowedMimeTypes = ALLOWED_MIME_TYPES[extension];
  if (file.type && !allowedMimeTypes.includes(file.type.toLowerCase())) {
    return `The file extension and format do not match. Choose a valid .${extension} file.`;
  }

  return null;
};

const hasBytes = (bytes: Uint8Array, signature: readonly number[]): boolean =>
  signature.every((value, index) => bytes[index] === value);

const getSvgSecurityError = (source: string): string | null => {
  if (!/<svg(?:\s|>)/i.test(source)) return 'The selected file is not a valid SVG image.';

  const unsafePatterns = [
    /<!doctype(?:\s|>)/i,
    /<!entity(?:\s|>)/i,
    /<script(?:\s|>)/i,
    /<foreignObject(?:\s|>)/i,
    /\son[a-z]+\s*=/i,
    /(?:href|xlink:href)\s*=\s*["']\s*(?:javascript:|data:text\/html|https?:\/\/|\/\/)/i,
    /@import\s/i,
    /url\(\s*["']?\s*(?:https?:\/\/|\/\/)/i,
  ];

  if (unsafePatterns.some((pattern) => pattern.test(source))) {
    return 'This SVG contains scripts or external content. Export a plain, self-contained SVG and try again.';
  }

  return null;
};

/**
 * Mirrors the Appwrite bucket rules and verifies that the bytes match the
 * selected extension. SVGs receive an additional active-content safety check.
 */
export const validateCompanyLogoFile = async (file: LogoFile): Promise<string | null> => {
  const metadataError = getCompanyLogoMetadataError(file);
  if (metadataError) return metadataError;

  const extension = getCompanyLogoExtension(file.name);
  if (!extension) return 'Choose a JPG, PNG, or SVG file.';

  const inspectionSize = extension === 'svg' ? file.size : 16;
  const bytes = new Uint8Array(await file.slice(0, inspectionSize).arrayBuffer());

  if (extension === 'jpg' && !hasBytes(bytes, [0xff, 0xd8, 0xff])) {
    return 'The selected file is not a valid JPG image.';
  }

  if (extension === 'png' && !hasBytes(bytes, PNG_SIGNATURE)) {
    return 'The selected file is not a valid PNG image.';
  }

  if (extension === 'svg') {
    return getSvgSecurityError(new TextDecoder('utf-8').decode(bytes));
  }

  return null;
};
